import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Plus, Edit, Trash2, Check, X, Activity } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DIAGNOSE_FEATURE_CATEGORIES = {
  basic: {
    title: 'Basis Features',
    features: [
      { key: 'basic_vehicle_database', label: 'Basis Fahrzeug Datenbank', description: 'Grundlegende Fahrzeugdaten' },
      { key: 'pdf_reports', label: 'PDF Reports', description: 'Diagnose-Reports als PDF' },
      { key: 'email_support', label: 'Email Support', description: 'Standard Email-Support' },
      { key: 'detailed_test_instructions', label: 'Prüfanweisungen', description: 'Mit Lokalisierung der Bauteile' }
    ]
  },
  advanced: {
    title: 'Erweiterte Features',
    features: [
      { key: 'extended_vehicle_database', label: 'Erweiterte Fahrzeugdatenbank', description: 'Umfangreiche Fahrzeugdaten' },
      { key: 'dual_ai_system', label: 'Duales KI System', description: 'Gemini 2.5 Pro + GPT-4o Validierung' },
      { key: 'custom_knowledge_base', label: 'Eigene Knowledge Base', description: 'Angepasste Wissensdatenbank' },
      { key: 'live_web_research', label: 'Live Web Recherche', description: 'Echtzeit-Websuche (Tavily)' },
      { key: 'priority_support', label: 'Prio Support', description: 'Bevorzugter Support' },
      { key: 'custom_branding', label: 'Custom Branding', description: 'Eigenes Logo in PDFs' }
    ]
  },
  diagnosis: {
    title: 'Diagnose Features',
    features: [
      { key: 'actual_value_assessment', label: 'Istwert Prüfung & Beurteilung', description: 'System beurteilt eingegebene Messwerte' },
      { key: 'repair_instructions', label: 'Reparatur Anleitung', description: 'Detaillierte Reparaturschritte mit Werkzeugen' },
      { key: 'file_uploads', label: 'Datei-Uploads (Bilder/Videos/Audio)', description: 'User können Bilder, Videos und Audio-Dateien hochladen' },
      { key: 'technical_specifications', label: 'Technische Fahrzeugdaten', description: 'KI-gesammelte technische Spezifikationen (Motor, Getriebe, etc.)' },
      { key: 'obd_live_diagnosis', label: '🚀 OBD Live-Diagnose (GAMECHANGER)', description: 'Remote Live-Diagnose mit CarCortex OBD-Dongle - Direkter Zugriff auf Fahrzeugdaten' }
    ]
  }
};

export const AdminDiagnosePlans = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPlan, setEditingPlan] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/admin/plans`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const diagnosePlans = response.data.filter(plan => plan.planType === 'diagnose' || !plan.planType);
      setPlans(diagnosePlans);
    } catch (error) {
      toast.error('Fehler beim Laden der Pläne');
      console.error('Fehler beim Laden der Pläne:', error);
    } finally {
      setLoading(false);
    }
  };

  const getDefaultFeatures = () => {
    const features = {};
    Object.values(DIAGNOSE_FEATURE_CATEGORIES).forEach(category => {
      category.features.forEach(feature => {
        features[feature.key] = false;
      });
    });
    return features;
  };

  const handleCreate = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Konvertiere Features Object für Backend (Boolean-Struktur)
      const featuresObj = editingPlan.features;

      const planData = {
        name: editingPlan.name,
        price: parseFloat(editingPlan.price),
        currency: editingPlan.currency === '€' ? 'EUR' : editingPlan.currency === '$' ? 'USD' : 'CHF',
        billing_interval: editingPlan.period === 'Jahr' ? 'yearly' : editingPlan.period === 'Einmalig' ? 'lifetime' : 'monthly',
        stripe_price_id: editingPlan.stripe_price_id || null,
        stripe_product_id: editingPlan.stripe_product_id || null,
        limits: {
          sessions_per_month: parseInt(editingPlan.sessionsPerMonth) || 0,
          storage_gb: 5,
          max_uploads_per_session: 10
        },
        features: featuresObj,
        is_active: true,
        sort_order: 0
      };

      await axios.post(
        `${API_URL}/api/admin/plans`,
        planData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Plan erfolgreich erstellt');
      fetchPlans();
      setShowCreateForm(false);
      setEditingPlan(null);
    } catch (error) {
      toast.error('Fehler beim Erstellen des Plans');
      console.error('Fehler beim Erstellen:', error);
    }
  };

  const handleUpdate = async () => {
    try {
      const token = localStorage.getItem('token');
      
      const planData = {
        id: editingPlan.id,
        name: editingPlan.name,
        price: parseFloat(editingPlan.price),
        currency: editingPlan.currency === '€' ? 'EUR' : editingPlan.currency === '$' ? 'USD' : 'CHF',
        billing_interval: editingPlan.period === 'Jahr' ? 'yearly' : editingPlan.period === 'Einmalig' ? 'lifetime' : 'monthly',
        stripe_price_id: editingPlan.stripe_price_id || null,
        stripe_product_id: editingPlan.stripe_product_id || null,
        limits: {
          sessions_per_month: parseInt(editingPlan.sessionsPerMonth) || 0,
          storage_gb: 5,
          max_uploads_per_session: 10
        },
        features: editingPlan.features,
        is_active: true,
        sort_order: 0
      };

      await axios.put(
        `${API_URL}/api/admin/plans/${editingPlan.id}`,
        planData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Plan erfolgreich aktualisiert');
      fetchPlans();
      setEditingPlan(null);
    } catch (error) {
      toast.error('Fehler beim Aktualisieren des Plans');
      console.error('Fehler beim Aktualisieren:', error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Plan wirklich löschen?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/admin/plans/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Plan gelöscht');
      fetchPlans();
    } catch (error) {
      toast.error('Fehler beim Löschen');
      console.error('Fehler beim Löschen:', error);
    }
  };

  const startEdit = (plan) => {
    // Features sind bereits ein Object im Backend
    const featuresObj = getDefaultFeatures();
    
    // Merge Backend-Features mit Default-Features
    if (plan.features && typeof plan.features === 'object') {
      Object.assign(featuresObj, plan.features);
    }

    setEditingPlan({
      id: plan.id,
      name: plan.name,
      price: plan.price.toString(),
      currency: plan.currency === 'EUR' ? '€' : plan.currency === 'USD' ? '$' : 'CHF',
      period: plan.billing_interval === 'yearly' ? 'Jahr' : plan.billing_interval === 'one_time' ? 'Einmalig' : 'Monat',
      sessionsPerMonth: plan.limits?.sessions_per_month?.toString() || '0',
      stripe_price_id: plan.stripe_price_id || '',
      stripe_product_id: plan.stripe_product_id || '',
      features: featuresObj,
      popular: plan.popular || false
    });
  };

  const startCreate = () => {
    setEditingPlan({
      name: '',
      price: '',
      currency: 'CHF',
      period: 'Monat',
      sessionsPerMonth: '',
      stripe_price_id: '',
      stripe_product_id: '',
      features: getDefaultFeatures(),
      popular: false,
      planType: 'diagnose'
    });
    setShowCreateForm(true);
  };

  const toggleFeature = (featureKey) => {
    setEditingPlan({
      ...editingPlan,
      features: {
        ...editingPlan.features,
        [featureKey]: !editingPlan.features[featureKey]
      }
    });
  };

  if (loading) return <div className="p-8">Lädt...</div>;

  return (
    <div className="p-8">
      <div className="flex justify-between items-center mb-8">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <Activity className="w-8 h-8" style={{ color: '#1EA0E6' }} />
            <h1 className="text-3xl font-bold" style={{ color: '#E6EDF3' }}>
              Diagnose-Pläne
            </h1>
          </div>
          <p style={{ color: '#94A3B8' }}>
            Verwalten Sie die Preispläne für Fahrzeugdiagnosen
          </p>
        </div>
        <Button
          onClick={startCreate}
          style={{ backgroundColor: '#1EA0E6', color: '#fff' }}
        >
          <Plus className="w-4 h-4 mr-2" />
          Neuer Diagnose-Plan
        </Button>
      </div>

      {(showCreateForm || editingPlan) && (
        <Card className="mb-8" style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
          <CardHeader>
            <CardTitle style={{ color: '#E6EDF3' }}>
              {showCreateForm ? 'Neuen Diagnose-Plan erstellen' : 'Diagnose-Plan bearbeiten'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block mb-2 text-sm font-semibold" style={{ color: '#E6EDF3' }}>
                  Plan-Name *
                </label>
                <Input
                  type="text"
                  value={editingPlan.name}
                  onChange={(e) => setEditingPlan({ ...editingPlan, name: e.target.value })}
                  placeholder="z.B. Basic, Pro, Unlimited"
                  style={{ backgroundColor: '#1F2937', color: '#E6EDF3', borderColor: '#374151' }}
                />
              </div>
              
              <div>
                <label className="block mb-2 text-sm font-semibold" style={{ color: '#E6EDF3' }}>
                  Preis *
                </label>
                <div className="flex gap-2">
                  <select
                    value={editingPlan.currency}
                    onChange={(e) => setEditingPlan({ ...editingPlan, currency: e.target.value })}
                    className="p-2 rounded-lg"
                    style={{ backgroundColor: '#1F2937', color: '#E6EDF3', borderColor: '#374151', border: '1px solid' }}
                  >
                    <option value="CHF">CHF</option>
                    <option value="€">€</option>
                    <option value="$">$</option>
                  </select>
                  <Input
                    type="number"
                    value={editingPlan.price}
                    onChange={(e) => setEditingPlan({ ...editingPlan, price: e.target.value })}
                    placeholder="19.99"
                    style={{ backgroundColor: '#1F2937', color: '#E6EDF3', borderColor: '#374151' }}
                  />
                </div>
              </div>

              <div>
                <label className="block mb-2 text-sm font-semibold" style={{ color: '#E6EDF3' }}>
                  Zeitraum *
                </label>
                <select
                  value={editingPlan.period}
                  onChange={(e) => setEditingPlan({ ...editingPlan, period: e.target.value })}
                  className="w-full p-2 rounded-lg"
                  style={{ backgroundColor: '#1F2937', color: '#E6EDF3', borderColor: '#374151', border: '1px solid' }}
                >
                  <option>Monat</option>
                  <option>Jahr</option>
                  <option>Einmalig</option>
                </select>
              </div>

              <div>
                <label className="block mb-2 text-sm font-semibold" style={{ color: '#E6EDF3' }}>
                  Diagnosen pro Monat
                  <span className="text-xs ml-2" style={{ color: '#94A3B8' }}>(0 = Unbegrenzt)</span>
                </label>
                <Input
                  type="number"
                  value={editingPlan.sessionsPerMonth}
                  onChange={(e) => setEditingPlan({ ...editingPlan, sessionsPerMonth: e.target.value })}
                  placeholder="10"
                  style={{ backgroundColor: '#1F2937', color: '#E6EDF3', borderColor: '#374151' }}
                />
              </div>
            </div>

            <div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={editingPlan.popular}
                  onChange={(e) => setEditingPlan({ ...editingPlan, popular: e.target.checked })}
                  className="w-5 h-5"
                />
                <span style={{ color: '#E6EDF3' }}>Als &quot;BELIEBT&quot; markieren</span>
              </label>
            </div>

            {/* Stripe Integration */}
            <div className="p-4 rounded-lg" style={{ backgroundColor: '#1E3A8A20', borderColor: '#3B82F6', border: '1px solid' }}>
              <div className="flex items-start gap-3">
                <div className="text-2xl">💳</div>
                <div>
                  <h4 className="font-semibold mb-1" style={{ color: '#7CD4FD' }}>
                    ✨ Automatische Stripe-Synchronisation
                  </h4>
                  <p className="text-sm mb-2" style={{ color: '#94A3B8' }}>
                    Wenn Stripe konfiguriert ist, wird automatisch ein Produkt und Preis in Ihrem Stripe-Dashboard erstellt.
                    Sie müssen keine Stripe Price ID manuell eingeben!
                  </p>
                  <p className="text-xs" style={{ color: '#6B7280' }}>
                    Optional: Geben Sie eine bestehende Stripe Price ID ein, um einen existierenden Stripe-Preis zu verwenden.
                  </p>
                </div>
              </div>
            </div>

            <div>
              <label className="block mb-2 text-sm font-semibold" style={{ color: '#E6EDF3' }}>
                Stripe Price ID (optional - wird automatisch erstellt)
              </label>
              <Input
                value={editingPlan.stripe_price_id || ''}
                onChange={(e) => setEditingPlan({ ...editingPlan, stripe_price_id: e.target.value })}
                placeholder="Leer lassen für automatische Erstellung"
                style={{ backgroundColor: '#1F2937', color: '#E6EDF3', borderColor: '#374151' }}
              />
              {editingPlan.stripe_price_id && (
                <p className="text-xs mt-1" style={{ color: '#10B981' }}>
                  ✓ Verwendet existierenden Stripe-Preis
                </p>
              )}
            </div>

            <div>
              <label className="block mb-2 text-sm font-semibold" style={{ color: '#E6EDF3' }}>
                Stripe Product ID (optional - wird automatisch erstellt)
              </label>
              <Input
                value={editingPlan.stripe_product_id || ''}
                onChange={(e) => setEditingPlan({ ...editingPlan, stripe_product_id: e.target.value })}
                placeholder="Leer lassen für automatische Erstellung"
                style={{ backgroundColor: '#1F2937', color: '#E6EDF3', borderColor: '#374151' }}
              />
              {editingPlan.stripe_product_id && (
                <p className="text-xs mt-1" style={{ color: '#10B981' }}>
                  ✓ Verwendet existierendes Stripe-Produkt
                </p>
              )}
            </div>

            {/* Features Selection */}
            <div className="space-y-4">
              <h3 className="text-xl font-bold mb-4" style={{ color: '#7CD4FD' }}>
                Inbegriffene Features
              </h3>

              {Object.entries(DIAGNOSE_FEATURE_CATEGORIES).map(([categoryKey, category]) => (
                <div key={categoryKey} className="p-4 rounded-lg" style={{ backgroundColor: '#1E293B', borderColor: '#1F2937', border: '1px solid' }}>
                  <h4 className="text-lg font-semibold mb-3" style={{ color: '#E6EDF3' }}>
                    {category.title}
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {category.features.map((feature) => (
                      <div
                        key={feature.key}
                        className="flex items-start gap-3 p-3 rounded-lg cursor-pointer transition-all hover:opacity-80"
                        onClick={() => toggleFeature(feature.key)}
                        style={{
                          backgroundColor: editingPlan.features[feature.key] ? '#1EA0E6' : '#0B0F14',
                          borderColor: '#1F2937',
                          border: '1px solid'
                        }}
                      >
                        <div className="flex-shrink-0 mt-1">
                          {editingPlan.features[feature.key] ? (
                            <Check className="w-5 h-5 text-white" />
                          ) : (
                            <X className="w-5 h-5 text-gray-500" />
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="font-semibold text-sm" style={{ color: editingPlan.features[feature.key] ? '#FFFFFF' : '#E6EDF3' }}>
                            {feature.label}
                          </div>
                          <div className="text-xs mt-1" style={{ color: editingPlan.features[feature.key] ? '#E6EDF3' : '#94A3B8' }}>
                            {feature.description}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                onClick={showCreateForm ? handleCreate : handleUpdate}
                style={{ backgroundColor: '#22C55E', color: '#fff' }}
              >
                <Check className="w-4 h-4 mr-2" />
                {showCreateForm ? 'Erstellen' : 'Aktualisieren'}
              </Button>
              <Button
                onClick={() => {
                  setShowCreateForm(false);
                  setEditingPlan(null);
                }}
                style={{ backgroundColor: '#374151', color: '#E6EDF3' }}
              >
                <X className="w-4 h-4 mr-2" />
                Abbrechen
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid md:grid-cols-3 gap-6">
        {plans.map((plan) => (
          <Card
            key={plan.id}
            style={{
              backgroundColor: '#0F141A',
              borderColor: plan.popular ? '#1EA0E6' : '#1F2937',
              borderWidth: plan.popular ? '2px' : '1px'
            }}
          >
            <CardContent className="p-6">
              {plan.popular && (
                <div className="mb-3 px-3 py-1 rounded-full inline-block text-xs font-bold" style={{ backgroundColor: '#1EA0E6', color: '#fff' }}>
                  BELIEBT
                </div>
              )}
              <h3 className="text-2xl font-bold mb-2" style={{ color: '#E6EDF3' }}>
                {plan.name}
              </h3>
              <div className="mb-4">
                <span className="text-4xl font-bold" style={{ color: '#7CD4FD' }}>
                  {plan.currency}{plan.price}
                </span>
                <span style={{ color: '#94A3B8' }}>/{plan.period}</span>
              </div>
              <div className="text-sm mb-4" style={{ color: '#94A3B8' }}>
                {plan.sessionsPerMonth > 0 ? `${plan.sessionsPerMonth} Diagnosen/Monat` : 'Unbegrenzte Diagnosen'}
              </div>
              
              {/* Features anzeigen */}
              {plan.features && plan.features.length > 0 && (
                <div className="mb-4 space-y-2">
                  {plan.features.slice(0, 3).map((feature, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-xs" style={{ color: '#94A3B8' }}>
                      <Check className="w-4 h-4" style={{ color: '#22C55E' }} />
                      <span>{feature}</span>
                    </div>
                  ))}
                  {plan.features.length > 3 && (
                    <div className="text-xs" style={{ color: '#7CD4FD' }}>
                      +{plan.features.length - 3} weitere Features
                    </div>
                  )}
                </div>
              )}

              <div className="flex gap-2 mt-4">
                <Button
                  size="sm"
                  onClick={() => startEdit(plan)}
                  style={{ backgroundColor: '#1EA0E6', color: '#fff' }}
                >
                  <Edit className="w-4 h-4" />
                </Button>
                <Button
                  size="sm"
                  onClick={() => handleDelete(plan.id)}
                  style={{ backgroundColor: '#EF4444', color: '#fff' }}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {plans.length === 0 && !showCreateForm && (
        <div className="text-center py-20">
          <Activity className="w-16 h-16 mx-auto mb-4" style={{ color: '#374151' }} />
          <p className="text-xl mb-2" style={{ color: '#94A3B8' }}>
            Noch keine Diagnose-Pläne erstellt
          </p>
          <p style={{ color: '#6B7280' }}>
            Klicken Sie auf &quot;Neuer Diagnose-Plan&quot;, um zu beginnen
          </p>
        </div>
      )}
    </div>
  );
};
